import { ViewHook } from "phoenix_live_view";
import { computePosition, autoUpdate, autoPlacement, shift, size } from "@floating-ui/dom";

const dropdownContentMaxHeight = 700;
const dropdownContentMaxWidth = 384;

const filterChangedEvent = "filter-changed";

class DropdownMenu extends ViewHook {
  private trigger?: HTMLElement;
  private content?: HTMLElement;

  private clickableItems?: NodeListOf<HTMLElement>;
  private itemEventListeners?: Map<HTMLElement, (event: MouseEvent) => void>;

  private clearButton?: HTMLElement;
  private clearSelectedFilterEventName?: string;

  private disableIfElement?: HTMLElement;
  private disableIfValue?: string;

  private wasOpenBeforeUpdate?: boolean;
  private positionBeforeUpdate?: { left: string; top: string; side?: string };

  private cleanup?: () => void;

  mounted() {
    this.itemEventListeners = new Map();

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onClearEvent = this.onClearEvent.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
    this.onClearSelectedFilter = this.onClearSelectedFilter.bind(this);
    this.onDisableIfFilterChanged = this.onDisableIfFilterChanged.bind(this);

    document.addEventListener("click", this.onClickOutside);
    this.syncClearSelectedFilterEvent();

    this.bindElements();

    if (this.el.dataset.initialState === "open") {
      this.open();
    } else {
      this.close();
    }
  }

  // LiveView patches wipe the client-managed open state (data-state, hidden, position styles),
  // so capture it before the patch and restore it synchronously in updated() to keep an open
  // dropdown open without flicker, e.g. while toggling multi-select items.
  beforeUpdate() {
    this.wasOpenBeforeUpdate = this.isOpen();

    if (this.wasOpenBeforeUpdate && this.content) {
      this.positionBeforeUpdate = {
        left: this.content.style.left,
        top: this.content.style.top,
        side: this.content.dataset.side,
      };
    }
  }

  updated() {
    const wasOpen = this.wasOpenBeforeUpdate ?? this.isOpen();

    this.wasOpenBeforeUpdate = undefined;

    this.unbindElements();
    this.cleanup?.();
    this.cleanup = undefined;

    this.syncClearSelectedFilterEvent();
    this.bindElements();

    if (wasOpen) {
      this.restoreOpenState();
      this.open();
    } else {
      this.close();
    }
  }

  private restoreOpenState() {
    const { content, positionBeforeUpdate } = this;

    /* v8 ignore start */
    if (!content) {
      return;
    }
    /* v8 ignore stop */

    if (positionBeforeUpdate) {
      content.style.left = positionBeforeUpdate.left;
      content.style.top = positionBeforeUpdate.top;

      if (positionBeforeUpdate.side) {
        content.dataset.side = positionBeforeUpdate.side;
      }
    }

    content.dataset.state = "open";
    content.removeAttribute("hidden");
  }

  destroyed() {
    this.unbindElements();
    this.removeClearSelectedFilterEvent();

    document.removeEventListener("click", this.onClickOutside);
    this.cleanup?.();
    this.cleanup = undefined;
  }

  private bindElements() {
    this.trigger = this.el.querySelector("[data-dropdown-menu-trigger]") as HTMLElement;
    this.content = this.el.querySelector("[data-dropdown-menu-content]") as HTMLElement;
    this.clickableItems = this.el.querySelectorAll("[data-dropdown-menu-item][data-clickable]");

    if (!this.trigger || !this.content) {
      throw new Error("DropdownMenu trigger or content not found");
    }

    this.trigger.addEventListener("click", this.onTriggerClick);

    this.clearButton = this.trigger.querySelector(
      "[data-dropdown-menu-clear-event]",
    ) as HTMLElement;

    this.clearButton?.addEventListener("click", this.onClearEvent);

    this.clickableItems.forEach((item) => {
      const eventHandler = (event: MouseEvent) => {
        this.onItemClick(event, item);
      };

      item.addEventListener("click", eventHandler);

      this.itemEventListeners?.set(item, eventHandler);
    });

    if (this.el.dataset.disableIfId && this.el.dataset.disableIfValue) {
      this.disableIfElement = document.querySelector(
        `#${this.el.dataset.disableIfId}`,
      ) as HTMLElement;

      if (this.disableIfElement) {
        this.disableIfValue = this.el.dataset.disableIfValue;

        this.disableIfElement.addEventListener(filterChangedEvent, this.onDisableIfFilterChanged);
      }
    }
  }

  private unbindElements() {
    this.trigger?.removeEventListener("click", this.onTriggerClick);
    this.clearButton?.removeEventListener("click", this.onClearEvent);

    this.itemEventListeners?.forEach((eventHandler, item) => {
      item.removeEventListener("click", eventHandler);
    });
    this.itemEventListeners?.clear();

    this.disableIfElement?.removeEventListener(filterChangedEvent, this.onDisableIfFilterChanged);

    this.trigger = undefined;
    this.content = undefined;
    this.clickableItems = undefined;
    this.clearButton = undefined;
    this.disableIfElement = undefined;
    this.disableIfValue = undefined;
  }

  private syncClearSelectedFilterEvent() {
    const { clearSelectedFilterEvent } = this.el.dataset;

    if (this.clearSelectedFilterEventName === clearSelectedFilterEvent) {
      return;
    }

    this.removeClearSelectedFilterEvent();

    if (clearSelectedFilterEvent) {
      document.addEventListener(clearSelectedFilterEvent, this.onClearSelectedFilter);
      this.clearSelectedFilterEventName = clearSelectedFilterEvent;
    }
  }

  private removeClearSelectedFilterEvent() {
    if (!this.clearSelectedFilterEventName) {
      return;
    }

    document.removeEventListener(this.clearSelectedFilterEventName, this.onClearSelectedFilter);
    this.clearSelectedFilterEventName = undefined;
  }

  private onTriggerClick() {
    this.toggle();
  }

  private onClearEvent(event: MouseEvent) {
    this.maybeClearSelectorLabel();

    const { clearButton } = this;
    /* v8 ignore start */
    if (!clearButton) {
      return;
    }
    /* v8 ignore stop */

    event.stopPropagation();

    this.dispatchValueChangedEvent();

    const { dropdownMenuClearEvent } = clearButton.dataset;
    /* v8 ignore start */
    if (dropdownMenuClearEvent) {
      this.maybeMarkAsLoading();

      void this.pushEvent(dropdownMenuClearEvent, {});
    }
    /* v8 ignore stop */

    this.close();
  }

  private onClearSelectedFilter() {
    /* v8 ignore next */
    if (this.trigger?.dataset.selected !== undefined) {
      this.maybeMarkAsLoading();
      this.maybeClearSelectorLabel();
    }
  }

  private onClickOutside(event: MouseEvent) {
    if (!this.isOpen()) {
      return;
    }

    const target = event.target as Node;

    const isOutside =
      this.trigger &&
      this.content &&
      !this.trigger.contains(target) &&
      !this.content.contains(target);

    if (isOutside) {
      this.close();
    }
  }

  private onItemClick(event: MouseEvent, item: HTMLElement) {
    if (item.dataset.multiSelect === undefined) {
      this.close();

      this.maybeSetSelectorLabel(item);
      this.dispatchValueChangedEvent(item.dataset.value);
    } else {
      this.toggleMultiSelectItem(item);
    }

    const { clickEvent, clickValueSlug: slug } = item.dataset;

    if (clickEvent) {
      event.preventDefault();
      this.maybeMarkAsLoading();

      void this.pushEvent(clickEvent, { slug });
    }
  }

  // Optimistic feedback only; the server patch re-renders the authoritative checked state.
  private toggleMultiSelectItem(item: HTMLElement) {
    const checked = item.dataset.state !== "checked";

    item.dataset.state = checked ? "checked" : "unchecked";
    item.setAttribute("aria-checked", `${checked}`);
  }

  private onDisableIfFilterChanged(event: Event) {
    const value = (event as CustomEvent<{ value?: string }>).detail?.value;

    if (value === this.disableIfValue) {
      this.maybeClearSelectorLabel();
      this.trigger?.setAttribute("disabled", "");
    } else {
      this.trigger?.removeAttribute("disabled");
    }
  }

  private maybeSetSelectorLabel(item: HTMLElement) {
    /* v8 ignore next */
    if (this.trigger?.dataset.selector === undefined) {
      return;
    }

    const itemLabel = item.dataset.label;
    const labelElement = this.trigger.querySelector("[data-selector-label]") as HTMLElement;

    /* v8 ignore start */
    if (!itemLabel || !labelElement) {
      return;
    }
    /* v8 ignore stop */

    labelElement.textContent = itemLabel;

    if (this.trigger.dataset.highlightSelected !== "false") {
      this.trigger.dataset.selected = item.dataset.value;
    }

    if (item.dataset.group) {
      this.trigger.dataset.group = item.dataset.group;
    }
  }

  private maybeClearSelectorLabel() {
    /* v8 ignore next */
    if (this.trigger?.dataset.selector === undefined) {
      return;
    }

    const labelElement = this.trigger.querySelector("[data-selector-label]") as HTMLElement;
    const defaultLabel = labelElement?.dataset.selectorLabel;

    /* v8 ignore start */
    if (!labelElement || !defaultLabel) {
      return;
    }
    /* v8 ignore stop */

    delete this.trigger.dataset.selected;
    labelElement.textContent = defaultLabel;
  }

  private maybeMarkAsLoading() {
    const { markAsLoadingId } = this.el.dataset;
    if (markAsLoadingId) {
      const markAsLoadingElement = document.querySelector(`#${markAsLoadingId}`) as HTMLElement;

      if (markAsLoadingElement) {
        markAsLoadingElement.dataset.loading = "";
      }
    }
  }

  private isOpen() {
    return this.content?.dataset.state === "open";
  }

  private toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  private open() {
    const { trigger, content } = this;

    /* v8 ignore start */
    if (!trigger || !content) {
      return;
    }
    /* v8 ignore stop */

    this.cleanup = autoUpdate(trigger, content, () => {
      void computePosition(trigger, content, {
        strategy: "fixed",
        middleware: [
          shift(),
          autoPlacement({
            allowedPlacements: [
              "top",
              "top-start",
              "top-end",
              "bottom",
              "bottom-start",
              "bottom-end",
            ],
          }),
          size({
            apply({ availableWidth, availableHeight, elements }) {
              Object.assign(elements.floating.style, {
                maxWidth: `${Math.min(dropdownContentMaxWidth, Math.max(0, availableWidth))}px`,
                maxHeight: `${Math.min(dropdownContentMaxHeight, Math.max(0, availableHeight))}px`,
              });
            },
          }),
        ],
      }).then(({ x, y, placement }) => {
        Object.assign(content.style, {
          left: `${x}px`,
          top: `${y}px`,
        });

        const [side] = placement.split("-");

        content.dataset.side = side;
        content.dataset.state = "open";

        if (content.hasAttribute("hidden")) {
          content.removeAttribute("hidden");

          // If we scroll the content in a scrollable dropdown like the industry list and then we
          // close the dropdown and open it again, the dropdown will show content as per the last
          // scroll. This makes sure we display the top of scroll when it's opened again.
          const scrollContainer = content.querySelector("[data-scroll-container]") as HTMLElement;
          if (scrollContainer) {
            scrollContainer.scrollTop = 0;
          }
        }
      });
    });
  }

  private close() {
    this.cleanup?.();

    const { content } = this;
    /* v8 ignore start */
    if (!content) {
      return;
    }
    /* v8 ignore stop */

    content.dataset.state = "closed";

    requestAnimationFrame(() => {
      // The animation runs for 150ms. Wait for a period just below that
      // to ensure the animation is complete.
      setTimeout(() => {
        content.setAttribute("hidden", "");
      }, 130);
    });
  }

  private dispatchValueChangedEvent(value?: string) {
    this.el.dispatchEvent(new CustomEvent(filterChangedEvent, { detail: { value } }));
  }
}

export default DropdownMenu;
